<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'

export default {
  page: {
    title: 'Prisma Cloud Priority Remediation',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    apexchart: VueApexCharts
  },
  data() {
    return {
      title: 'Priority Remediation - Critical Vulnerabilities',
      loading: true,
      updated: '',

      // Stats data
      stats: {
        total_critical_old: 0,
        exploitable_exposed: 0,
        oldest_days: 0,
        affected_assets: 0,
        avg_age_days: 0,
        total_critical: 0
      },

      // Shame data
      oldestCritical: [],
      worstAssets: [],
      ageDistribution: [],

      // Filter options
      minAgeDays: '30',
      ageOptions: [
        { value: '30', text: '30+ Days' },
        { value: '60', text: '60+ Days' },
        { value: '90', text: '90+ Days' },
        { value: '180', text: '180+ Days' },
        { value: '365', text: '1+ Year' }
      ],

      selectedServiceProvider: 'all',
      serviceProviders: [],

      showOnlyExploitable: false,
      showOnlyInternetExposed: false,
      showOnlyKev: false,
      showOnlyBoth: false,
      showOnlyAllThree: true,

      searchQuery: '',

      // Priority Score tooltip
      priorityScoreTooltip: 'Priority Score Calculation:\n• Base: CVSS Score (0-10) × 10\n• +20 if Exploitable\n• +20 if Internet Exposed\n• +15 if CISA KEV\n• +Age bonus (up to 25 points)\n\nMax score: 100',

      // Chart options
      ageDistributionChartOptions: {
        chart: {
          type: 'bar',
          height: 280,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            distributed: false,
            columnWidth: '60%'
          }
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '12px', colors: ['#fff'] }
        },
        xaxis: {
          categories: [],
          title: { text: 'Age Range' }
        },
        yaxis: {
          title: { text: 'Critical CVE Count' }
        },
        colors: ['#802020'],
        legend: { show: false }
      },
      ageDistributionChartSeries: [],

      // Table configuration
      columns: [
        { key: 'CVE_ID', label: 'CVE ID', sortable: true },
        { key: 'ASSET_NAME', label: 'Asset', sortable: true },
        { key: 'OWNER', label: 'Owner', sortable: true },
        { key: 'AGE_DAYS', label: 'Age (Days)', sortable: true },
        { key: 'PUBLISHED_DATE', label: 'Published', sortable: true },
        { key: 'CVSS_BASE_SCORE', label: 'CVSS', sortable: true },
        { key: 'EXPLOITABLE', label: 'Exploitable', sortable: true },
        { key: 'INTERNET_EXPOSED', label: 'Exposed', sortable: true },
        { key: 'CISA_KEV', label: 'KEV', sortable: true },
        { key: 'PACKAGE_NAME', label: 'Package', sortable: true },
        { key: 'SERVICE_PROVIDER', label: 'Provider', sortable: true },
        { key: 'SHAME_SCORE', label: 'Priority Score', sortable: true, hasTooltip: true }
      ],

      sortKey: 'SHAME_SCORE',
      sortDirection: 'desc',

      // Pagination
      currentPage: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 0,

      // Export and loading states
      exporting: false,
      tableLoading: false,

      items: [
        { text: 'Home', href: '/' },
        { text: 'Prisma Cloud', href: '/prismacloud' },
        { text: 'Priority Remediation', active: true }
      ],

      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev')
    }
  },

  computed: {
    filterParams() {
      const params = {
        min_age_days: this.minAgeDays
      }

      if (this.selectedServiceProvider !== 'all') {
        params.service_provider = this.selectedServiceProvider
      }

      if (this.showOnlyAllThree) {
        params.exploitable = 'Y'
        params.internet_exposed = 'Y'
        params.cisa_kev = 'Y'
      } else if (this.showOnlyBoth) {
        params.exploitable = 'Y'
        params.internet_exposed = 'Y'
      } else {
        if (this.showOnlyExploitable) {
          params.exploitable = 'Y'
        }

        if (this.showOnlyInternetExposed) {
          params.internet_exposed = 'Y'
        }

        if (this.showOnlyKev) {
          params.cisa_kev = 'Y'
        }
      }

      if (this.searchQuery) {
        params.search = this.searchQuery
      }

      return params
    }
  },

  watch: {
    minAgeDays: { handler() { this.loadAllData() } },
    selectedServiceProvider: { handler() { this.loadAllData() } },
    showOnlyExploitable: { handler() { this.loadAllData() } },
    showOnlyInternetExposed: { handler() { this.loadAllData() } },
    showOnlyKev: { handler() { this.loadAllData() } },
    showOnlyBoth: { handler() { this.loadAllData() } },
    showOnlyAllThree: { handler() { this.loadAllData() } },
    searchQuery: {
      handler() { this.debouncedSearch() },
      immediate: false
    }
  },

  mounted() {
    this.loadServiceProviders()
    this.loadAllData()
  },

  methods: {
    buildQueryString(params) {
      return Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
    },

    debouncedSearch: (function() {
      let timeout = null
      return function() {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          this.loadAllData()
        }, 500)
      }
    })(),

    async loadServiceProviders() {
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      try {
        const response = await axios.get(`${baseURL}/prismacloud/vulnerabilities/service-providers`)
        if (response.data && response.data.service_providers) {
          this.serviceProviders = [
            { value: 'all', text: 'All Providers' },
            ...response.data.service_providers.map(sp => ({ value: sp, text: sp }))
          ]
        }
      } catch (error) {
        console.error('Error loading service providers:', error)
      }
    },

    async loadAllData() {
      this.loading = true
      this.tableLoading = true
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const queryString = this.buildQueryString(this.filterParams)

      try {
        const [
          statsResponse,
          oldestResponse,
          worstAssetsResponse,
          ageDistResponse,
          listResponse
        ] = await Promise.all([
          axios.get(`${baseURL}/prismacloud/hallofshame/stats?${queryString}`),
          axios.get(`${baseURL}/prismacloud/hallofshame/oldest-critical?${queryString}`),
          axios.get(`${baseURL}/prismacloud/hallofshame/worst-assets?${queryString}`),
          axios.get(`${baseURL}/prismacloud/hallofshame/age-distribution?${queryString}`),
          axios.get(`${baseURL}/prismacloud/hallofshame/list?${queryString}&page=${this.currentPage}&page_size=${this.pageSize}&sort_by=${this.sortKey}&sort_dir=${this.sortDirection}`)
        ])

        if (statsResponse.data) {
          this.stats = statsResponse.data
        }

        if (oldestResponse.data && oldestResponse.data.vulnerabilities) {
          this.oldestCritical = oldestResponse.data.vulnerabilities
        }

        if (worstAssetsResponse.data && worstAssetsResponse.data.assets) {
          this.worstAssets = worstAssetsResponse.data.assets
        }

        if (ageDistResponse.data && ageDistResponse.data.distribution) {
          this.renderAgeDistributionChart(ageDistResponse.data.distribution)
        }

        if (listResponse.data) {
          this.oldestCritical = listResponse.data.vulnerabilities || []
          this.totalCount = listResponse.data.total_count || 0
          this.totalPages = listResponse.data.total_pages || 0
        }

        this.updated = new Date().toLocaleString()
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
        this.tableLoading = false
      }
    },

    renderAgeDistributionChart(distribution) {
      this.ageDistributionChartOptions = {
        ...this.ageDistributionChartOptions,
        xaxis: {
          ...this.ageDistributionChartOptions.xaxis,
          categories: distribution.map(d => d._id)
        }
      }
      this.ageDistributionChartSeries = [{
        name: 'Critical CVEs',
        data: distribution.map(d => d.count)
      }]
    },

    getShameScoreClass(score) {
      if (score >= 90) return 'badge badge-severity-critical fs-6'
      if (score >= 70) return 'badge badge-severity-high fs-6'
      if (score >= 50) return 'badge badge-severity-medium fs-6'
      return 'badge badge-severity-low fs-6'
    },

    getShameScoreLabel(score) {
      if (score >= 90) return 'CRITICAL PRIORITY'
      if (score >= 70) return 'HIGH PRIORITY'
      if (score >= 50) return 'MEDIUM PRIORITY'
      return 'LOW PRIORITY'
    },

    getYesNoClass(value) {
      return value === 'Y' ? 'badge badge-yes' : 'badge badge-no'
    },

    getKevClass(value) {
      return value === 'Y' ? 'badge badge-yes' : 'badge badge-no'
    },

    getRowClass(vuln) {
      const isExploitable = vuln.EXPLOITABLE === 'Y'
      const isExposed = vuln.INTERNET_EXPOSED === 'Y'
      const isKev = vuln.CISA_KEV === 'Y'

      // All three - highest risk
      if (isExploitable && isExposed && isKev) {
        return 'table-danger-critical'
      }
      // Exploitable + Internet Exposed
      if (isExploitable && isExposed) {
        return 'table-danger'
      }
      return ''
    },

    extractYearFromCVE(cveId) {
      if (!cveId) return null
      // CVE format: CVE-YYYY-NNNNN
      const match = cveId.match(/CVE-(\d{4})-\d+/)
      return match ? parseInt(match[1]) : null
    },

    formatDate(dateStr, cveId) {
      try {
        let date = null

        // Try to parse the provided date
        if (dateStr) {
          let dateValue = dateStr

          // MongoDB extended JSON format
          if (dateStr.$date) {
            dateValue = dateStr.$date
          }

          // If it's an object with $numberLong (MongoDB timestamp)
          if (dateStr.$date && dateStr.$date.$numberLong) {
            dateValue = parseInt(dateStr.$date.$numberLong)
          }

          date = new Date(dateValue)

          // Check if date is valid
          if (isNaN(date.getTime())) {
            date = null
          }
        }

        // Fallback: extract year from CVE_ID and use January 1st
        if (!date && cveId) {
          const year = this.extractYearFromCVE(cveId)
          if (year) {
            date = new Date(year, 0, 1) // January 1st of the CVE year
          }
        }

        if (!date) return 'N/A'

        return date.toLocaleDateString()
      } catch (e) {
        return 'N/A'
      }
    },

    calculateDaysOld(dateStr, cveId) {
      try {
        let date = null

        // Try to parse the provided date
        if (dateStr) {
          let dateValue = dateStr

          // MongoDB extended JSON format
          if (dateStr.$date) {
            dateValue = dateStr.$date
          }

          // If it's an object with $numberLong (MongoDB timestamp)
          if (dateStr.$date && dateStr.$date.$numberLong) {
            dateValue = parseInt(dateStr.$date.$numberLong)
          }

          date = new Date(dateValue)

          // Check if date is valid
          if (isNaN(date.getTime())) {
            date = null
          }
        }

        // Fallback: extract year from CVE_ID and use January 1st
        if (!date && cveId) {
          const year = this.extractYearFromCVE(cveId)
          if (year) {
            date = new Date(year, 0, 1) // January 1st of the CVE year
          }
        }

        if (!date) return 'N/A'

        const now = new Date()
        const diffTime = Math.abs(now - date)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        return diffDays
      } catch (e) {
        return 'N/A'
      }
    },

    toggleSort(key) {
      const column = this.columns.find(col => col.key === key)
      if (!column || !column.sortable) return

      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDirection = 'desc'
      }

      this.loadAllData()
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return 'mdi mdi-sort'
      return this.sortDirection === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending'
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.loadAllData()
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },

    async exportToCsv() {
      this.exporting = true
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const queryString = this.buildQueryString(this.filterParams)

      try {
        // Fetch all vulnerabilities (no pagination)
        const response = await axios.get(
          `${baseURL}/prismacloud/hallofshame/list?${queryString}&page=1&page_size=${this.totalCount || 10000}&sort_by=${this.sortKey}&sort_dir=${this.sortDirection}`
        )

        if (!response.data || !response.data.vulnerabilities) {
          console.error('No data to export')
          return
        }

        const vulnerabilities = response.data.vulnerabilities

        // Define CSV headers
        const headers = [
          'CVE ID',
          'Asset Name',
          'Owner',
          'Age (Days)',
          'Published Date',
          'CVSS Score',
          'Exploitable',
          'Internet Exposed',
          'CISA KEV',
          'Package Name',
          'Service Provider',
          'Priority Score'
        ]

        // Convert data to CSV rows
        const rows = vulnerabilities.map(vuln => [
          vuln.CVE_ID || '',
          vuln.ASSET_NAME || '',
          vuln.OWNER || '',
          vuln.AGE_DAYS || this.calculateDaysOld(vuln.PUBLISHED_DATE, vuln.CVE_ID) || '',
          this.formatDate(vuln.PUBLISHED_DATE, vuln.CVE_ID) || '',
          vuln.CVSS_BASE_SCORE || '',
          vuln.EXPLOITABLE || '',
          vuln.INTERNET_EXPOSED || '',
          vuln.CISA_KEV || '',
          vuln.PACKAGE_NAME || '',
          vuln.SERVICE_PROVIDER || '',
          (vuln.SHAME_SCORE || 0).toFixed(0)
        ])

        // Build CSV content
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n')

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `prismacloud_priority_remediation_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error exporting CSV:', error)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container" style="max-width: 1600px !important;">

      <!-- Warning Banner -->
      <div class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
        <i class="mdi mdi-alert-octagon me-2"></i>
        <strong>Priority Remediation:</strong> These critical vulnerabilities have been left unpatched for an extended period.
        Vulnerabilities marked as exploitable, internet-exposed, or in CISA's Known Exploited Vulnerabilities (KEV) catalog pose the highest risk.
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>

      <!-- Export Button -->
      <div class="mb-3">
        <b-button
          class="btn btn-primary btn-sm"
          variant="light"
          :disabled="exporting || totalCount === 0"
          @click="exportToCsv"
        >
          <feather type="file-text" class="icon-xs mr-1"></feather>
          <span v-if="exporting">Exporting...</span>
          <span v-else>Export CSV</span>
        </b-button>
      </div>

      <!-- Filters Panel -->
      <div class="card mb-4 filter-panel">
        <div class="card-body py-3">
          <div class="row g-3 align-items-end">
            <!-- Minimum Age -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Minimum Age</label>
              <select class="form-select form-select-sm" v-model="minAgeDays">
                <option v-for="opt in ageOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Service Provider -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Service Provider</label>
              <select class="form-select form-select-sm" v-model="selectedServiceProvider">
                <option v-for="opt in serviceProviders" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Search -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Search</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="searchQuery"
                placeholder="CVE, Asset, Package..."
              >
            </div>

            <!-- Filter toggles -->
            <div class="col-lg-6 col-md-12">
              <label class="filter-label">Risk Filters</label>
              <div class="risk-filters">
                <div class="risk-filter-option risk-filter-critical">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="filterAllThree"
                    v-model="showOnlyAllThree"
                  >
                  <label class="form-check-label" for="filterAllThree">
                    Exploitable + Exposed + KEV (Worst)
                  </label>
                </div>
                <div class="risk-filter-option risk-filter-high">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="filterBoth"
                    v-model="showOnlyBoth"
                    :disabled="showOnlyAllThree"
                  >
                  <label class="form-check-label" for="filterBoth">
                    Exploitable + Internet Exposed
                  </label>
                </div>
                <div class="risk-filter-option">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="filterExploitable"
                    v-model="showOnlyExploitable"
                    :disabled="showOnlyBoth || showOnlyAllThree"
                  >
                  <label class="form-check-label" for="filterExploitable">
                    Exploitable
                  </label>
                </div>
                <div class="risk-filter-option">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="filterExposed"
                    v-model="showOnlyInternetExposed"
                    :disabled="showOnlyBoth || showOnlyAllThree"
                  >
                  <label class="form-check-label" for="filterExposed">
                    Internet Exposed
                  </label>
                </div>
                <div class="risk-filter-option risk-filter-kev">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="filterKev"
                    v-model="showOnlyKev"
                    :disabled="showOnlyBoth || showOnlyAllThree"
                  >
                  <label class="form-check-label" for="filterKev">
                    CISA KEV
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-12 text-end">
              <span class="updated-text">Updated: {{ updated }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-danger" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2">Loading Priority Remediation data...</p>
      </div>

      <div v-else>
        <!-- Stats Cards -->
        <div class="row mb-4">
          <div class="col-md-2">
            <div class="card mini-stats-wid border-danger">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Critical Old CVEs</p>
                    <h4 class="mb-0 text-danger">{{ stats.total_critical_old.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-danger">
                    <span class="avatar-title">
                      <feather type="alert-octagon" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid border-danger">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Exploitable + Exposed</p>
                    <h4 class="mb-0 text-danger">{{ stats.exploitable_exposed.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-danger">
                    <span class="avatar-title">
                      <feather type="zap" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Oldest CVE (Days)</p>
                    <h4 class="mb-0 text-warning">{{ stats.oldest_days.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-warning">
                    <span class="avatar-title">
                      <feather type="clock" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Avg Age (Days)</p>
                    <h4 class="mb-0">{{ stats.avg_age_days.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-info">
                    <span class="avatar-title">
                      <feather type="calendar" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Affected Assets</p>
                    <h4 class="mb-0">{{ stats.affected_assets.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-secondary">
                    <span class="avatar-title">
                      <feather type="server" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Total Critical</p>
                    <h4 class="mb-0">{{ stats.total_critical.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-primary">
                    <span class="avatar-title">
                      <feather type="shield" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="row mb-4">
          <div class="col-md-12">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Critical Vulnerabilities by Age Range</h5>
                  <div class="severity-legend">
                    <span class="legend-item"><span class="legend-color bg-severity-critical"></span>Critical</span>
                  </div>
                </div>
                <apexchart
                  v-if="ageDistributionChartSeries.length > 0"
                  height="280"
                  :options="ageDistributionChartOptions"
                  :series="ageDistributionChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Hall of Shame Table -->
      <div class="card position-relative">
        <!-- Table Loading Overlay -->
        <div v-if="tableLoading" class="table-loading-overlay">
          <div class="spinner-border text-danger" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 mb-0 text-muted">Loading priority remediation data...</p>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title mb-0">
              <i class="mdi mdi-fire text-danger me-2"></i>
              Priority Remediation ({{ totalCount.toLocaleString() }} critical CVEs)
            </h5>
            <div>
              <span class="text-muted me-3">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                class="btn btn-sm btn-outline-secondary me-1"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th
                    v-for="col in columns"
                    :key="col.key"
                    :class="{ 'sortable': col.sortable, 'active': sortKey === col.key }"
                    @click="col.sortable && toggleSort(col.key)"
                    style="cursor: pointer;"
                  >
                    <span v-if="col.hasTooltip" v-b-tooltip.hover.top :title="priorityScoreTooltip" class="header-with-tooltip">
                      {{ col.label }}
                      <i class="mdi mdi-help-circle-outline ms-1 text-muted"></i>
                    </span>
                    <span v-else>{{ col.label }}</span>
                    <i v-if="col.sortable" :class="getSortIcon(col.key)" class="ms-1"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(vuln, index) in oldestCritical" :key="index" :class="getRowClass(vuln)">
                  <td>
                    <a v-if="vuln.CVE_LINK && vuln.CVE_LINK !== 'N/A'" :href="vuln.CVE_LINK" target="_blank" class="text-danger fw-bold">
                      {{ vuln.CVE_ID }}
                    </a>
                    <span v-else class="text-danger fw-bold">{{ vuln.CVE_ID }}</span>
                  </td>
                  <td class="col-asset">
                    <span v-b-tooltip.hover.top :title="vuln.ASSET_NAME || ''">{{ truncateText(vuln.ASSET_NAME, 30) }}</span>
                  </td>
                  <td class="col-owner">
                    <span v-b-tooltip.hover.top :title="vuln.OWNER || 'N/A'">{{ truncateText(vuln.OWNER, 12) || 'N/A' }}</span>
                  </td>
                  <td>
                    <span class="badge badge-age">{{ vuln.AGE_DAYS || calculateDaysOld(vuln.PUBLISHED_DATE, vuln.CVE_ID) }}</span>
                  </td>
                  <td>{{ formatDate(vuln.PUBLISHED_DATE, vuln.CVE_ID) }}</td>
                  <td><span class="badge badge-severity-critical">{{ vuln.CVSS_BASE_SCORE || 'N/A' }}</span></td>
                  <td>
                    <span :class="getYesNoClass(vuln.EXPLOITABLE)">{{ vuln.EXPLOITABLE }}</span>
                  </td>
                  <td>
                    <span :class="getYesNoClass(vuln.INTERNET_EXPOSED)">{{ vuln.INTERNET_EXPOSED }}</span>
                  </td>
                  <td>
                    <span :class="getKevClass(vuln.CISA_KEV)">{{ vuln.CISA_KEV || 'N' }}</span>
                  </td>
                  <td class="col-package">
                    <span v-b-tooltip.hover.top :title="vuln.PACKAGE_NAME || ''">{{ truncateText(vuln.PACKAGE_NAME, 20) }}</span>
                  </td>
                  <td class="col-provider">
                    <span v-b-tooltip.hover.top :title="vuln.SERVICE_PROVIDER || 'N/A'">{{ truncateText(vuln.SERVICE_PROVIDER, 12) || 'N/A' }}</span>
                  </td>
                  <td>
                    <span :class="getShameScoreClass(vuln.SHAME_SCORE || 0)">
                      {{ (vuln.SHAME_SCORE || 0).toFixed(0) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="oldestCritical.length === 0">
                  <td :colspan="columns.length" class="text-center py-4 text-success">
                    <i class="mdi mdi-check-circle me-2"></i>
                    No critical vulnerabilities found matching your criteria. Great job!
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-center mt-3" v-if="totalPages > 1">
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(1)">First</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Prev</a>
                </li>
                <li
                  v-for="page in Math.min(5, totalPages)"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">Last</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Filter Panel Styles */
.filter-panel {
  background: #fff;
  border: 1px solid #e9ecef;
}

.filter-panel .card-body {
  padding: 1rem 1.25rem;
}

.filter-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.35rem;
}

.filter-panel .form-control,
.filter-panel .form-select {
  height: 34px;
  font-size: 0.85rem;
  border-color: #dee2e6;
}

.filter-panel .form-control:focus,
.filter-panel .form-select:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.15rem rgba(220, 53, 69, 0.15);
}

.updated-text {
  font-size: 0.8rem;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
}

/* Stats Cards */
.mini-stats-wid .card-body {
  padding: 1rem;
}

.mini-stats-wid.border-danger {
  border-left: 4px solid #dc3545 !important;
}

.mini-stat-icon {
  width: 48px;
  height: 48px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
}

.avatar-title .icon-sm {
  width: 20px;
  height: 20px;
}

/* Table Styles */
.sortable {
  cursor: pointer;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sortable.active {
  background-color: rgba(0, 0, 0, 0.075);
}

.table th {
  font-size: 0.85rem;
  white-space: nowrap;
}

.table td {
  font-size: 0.85rem;
  vertical-align: middle;
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.table-danger-critical {
  background-color: rgba(128, 32, 32, 0.2) !important;
}

/* Header with tooltip */
.header-with-tooltip {
  cursor: help;
  border-bottom: 1px dotted #6c757d;
}

.badge {
  font-size: 0.75rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

.fs-6 {
  font-size: 0.9rem !important;
}

/* Alert banner */
.alert-danger {
  border-left: 4px solid #dc3545;
}

/* Risk Filters */
.risk-filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  padding-top: 2px;
}

.risk-filter-option {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 4px;
  font-size: 0.85rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  transition: all 0.15s ease;
}

.risk-filter-option:hover {
  background: #e9ecef;
}

.risk-filter-option .form-check-input {
  margin-right: 0.5rem;
  margin-top: 0;
}

.risk-filter-option .form-check-label {
  margin-bottom: 0;
  font-size: 0.85rem;
  cursor: pointer;
}

.risk-filter-critical {
  border-color: #802020;
  background: rgba(128, 32, 32, 0.08);
}

.risk-filter-critical:hover {
  background: rgba(128, 32, 32, 0.15);
}

.risk-filter-critical .form-check-label {
  color: #802020;
  font-weight: 600;
}

.risk-filter-high {
  border-color: #DC001E;
  background: rgba(220, 0, 30, 0.08);
}

.risk-filter-high:hover {
  background: rgba(220, 0, 30, 0.15);
}

.risk-filter-high .form-check-label {
  color: #DC001E;
  font-weight: 500;
}

.risk-filter-kev {
  border-color: #E57373;
  background: rgba(229, 115, 115, 0.08);
}

.risk-filter-kev:hover {
  background: rgba(229, 115, 115, 0.15);
}

.risk-filter-kev .form-check-label {
  color: #E57373;
  font-weight: 500;
}

.risk-filter-option .form-check-input:disabled + .form-check-label {
  opacity: 0.5;
}

/* Severity Legend */
.severity-legend {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6c757d;
}

.legend-color {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
}

.bg-severity-critical {
  background-color: #802020;
}

.bg-severity-high {
  background-color: #DC001E;
}

.bg-severity-medium {
  background-color: #FECA00;
}

.bg-severity-low {
  background-color: #01AD00;
}

/* Badge severity colors for table */
.badge-severity-critical {
  background-color: #802020;
  color: #fff;
}

.badge-severity-high {
  background-color: #DC001E;
  color: #fff;
}

.badge-severity-medium {
  background-color: #FECA00;
  color: #212529;
}

.badge-severity-low {
  background-color: #01AD00;
  color: #fff;
}

/* Age badge */
.badge-age {
  background-color: #5470C6;
  color: #fff;
}

/* Yes/No badge colors */
.badge-yes {
  background-color: #E57373;
  color: #fff;
}

.badge-no {
  background-color: #81C784;
  color: #fff;
}

/* Button icon styling */
.icon-xs {
  width: 14px;
  height: 14px;
  vertical-align: middle;
}

.mr-1 {
  margin-right: 0.25rem;
}

/* Table Loading Overlay */
.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.25rem;
}

.table-loading-overlay .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
}

/* Tooltip columns */
.col-asset span,
.col-owner span,
.col-package span,
.col-provider span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.col-asset {
  max-width: 200px;
}

.col-owner {
  max-width: 100px;
}

.col-package {
  max-width: 150px;
}

.col-provider {
  max-width: 100px;
}
</style>
